import React from "react";
import { Button, DatePicker, Input, InputNumber } from "antd";
import { CopyOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

export type PaymentRow = {
  _key: string;
  amount: number | null;
  date: string;
  note: string;
};

let keyCounter = 0;
export const createPaymentRow = (overrides: Partial<PaymentRow> = {}): PaymentRow => ({
  _key: `pr-${Date.now()}-${keyCounter++}`,
  amount: null,
  date: dayjs().format("YYYY-MM-DD"),
  note: "",
  ...overrides,
});

type Props = {
  rows: PaymentRow[];
  onChange: (rows: PaymentRow[]) => void;
  isMobile?: boolean;
  minRows?: number;
};

const PaymentRowsEditor: React.FC<Props> = ({
  rows,
  onChange,
  isMobile,
  minRows = 1,
}) => {
  const { t } = useTranslation();

  const updateRow = (key: string, patch: Partial<PaymentRow>) => {
    onChange(rows.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  };

  const handleDuplicate = (index: number) => {
    const source = rows[index];
    const duplicate = createPaymentRow({
      amount: source.amount,
      note: source.note,
      date: dayjs(source.date).add(1, "month").format("YYYY-MM-DD"),
    });
    const next = [...rows];
    next.splice(index + 1, 0, duplicate);
    onChange(next);
  };

  const handleAddBlank = () => {
    const last = rows[rows.length - 1];
    onChange([
      ...rows,
      createPaymentRow({
        date: last
          ? dayjs(last.date).add(1, "month").format("YYYY-MM-DD")
          : undefined,
      }),
    ]);
  };

  const handleRemove = (key: string) => {
    if (rows.length > minRows) {
      onChange(rows.filter((r) => r._key !== key));
    }
  };

  return (
    <div>
      {rows.map((row, index) => (
        <div
          key={row._key}
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 8,
            alignItems: "flex-start",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          <DatePicker
            value={row.date ? dayjs(row.date) : null}
            format="DD.MM.YYYY"
            allowClear={false}
            onChange={(d) =>
              updateRow(row._key, { date: d ? d.format("YYYY-MM-DD") : row.date })
            }
            style={{ width: isMobile ? "100%" : 140 }}
          />
          <InputNumber
            value={row.amount ?? undefined}
            min={0}
            step={50}
            placeholder={t("Amount")}
            onChange={(v) => updateRow(row._key, { amount: v === null ? null : Number(v) })}
            style={{ width: isMobile ? "100%" : 130 }}
          />
          <Input
            value={row.note}
            placeholder={t("Note (optional)")}
            onChange={(e) => updateRow(row._key, { note: e.target.value })}
            style={{ flex: 1, minWidth: isMobile ? "100%" : 120 }}
          />
          <Button
            icon={<CopyOutlined />}
            onClick={() => handleDuplicate(index)}
            title={t("Duplicate as next month's installment")}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            disabled={rows.length <= minRows}
            onClick={() => handleRemove(row._key)}
            title={t("Remove")}
          />
        </div>
      ))}

      <Button
        type="dashed"
        block
        icon={<PlusOutlined />}
        onClick={handleAddBlank}
        style={{ marginTop: 4 }}
      >
        {t("Add another payment")}
      </Button>
    </div>
  );
};

export default PaymentRowsEditor;
